import { HttpClient, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, catchError, combineLatest, Observable, of } from 'rxjs';
import { PublicAsset as Asset } from '../../../../../../api/src/asset/asset.entity';
import { Type as AssetCategory } from '../../../../../../api/src/asset/asset.entity';
import { DefaultResponse, SessionQuery } from '../session';
import { AssetStore } from './asset.store';
import { QueryParams } from '../collection/collection.model';
import { BusinessQuery } from '../business';
import { ProjectsQuery } from '../project';
import { CreativeUnitPackageQuery } from '../ad/ad-unit-package.query';
import { GlobalService } from '../global';

export type FileUploadObject = {
	id?: string;
	title?: string;
	description?: string;
	dimensions?: any;
	mimeType?: string;
	saveTo?: 'my-files' | 'dealer-files' | 'project-files' | 'all-files';
	category?: AssetCategory;
	uploaded?: boolean;
	uploading?: boolean;
	duration?: number;
	file: File;
	collectionIds?: string[];
	progress?: number; // Upload progress percentage (0-100)
	bytesLoaded?: number; // Bytes uploaded so far
	totalBytes?: number; // Total bytes to upload
	error?: string; // Error message if upload failed
};

export enum AssetGroupKey {
	userAssets = 'userAssets',
	projectAssets = 'projectAssets',
	businessAssets = 'businessAssets',
	siteAssets = 'siteAssets',
	userProjectExports = 'userProjectExports',
}

@Injectable({ providedIn: 'root' })
export class AssetService {
	private assetListUser: BehaviorSubject<Asset[]> = new BehaviorSubject<Asset[]>([]);
	public get assetListUser$(): Observable<Asset[]> {
		return this.assetListUser.asObservable();
	}
	private assetListUserProjectExports: BehaviorSubject<Asset[]> = new BehaviorSubject<Asset[]>([]);
	public get assetListUserProjectExports$(): Observable<Asset[]> {
		return this.assetListUserProjectExports.asObservable();
	}
	private assetListProject: BehaviorSubject<Asset[]> = new BehaviorSubject<Asset[]>([]);
	public get assetListProject$(): Observable<Asset[]> {
		return this.assetListProject.asObservable();
	}

	private assetListBusiness: BehaviorSubject<Asset[]> = new BehaviorSubject<Asset[]>([]);
	public get assetListBusiness$(): Observable<Asset[]> {
		return this.assetListBusiness.asObservable();
	}
	private assetListSite: BehaviorSubject<Asset[]> = new BehaviorSubject<Asset[]>([]);
	public get assetListSite$(): Observable<Asset[]> {
		return this.assetListSite.asObservable();
	}

	constructor(
		private readonly http: HttpClient,
		private readonly businessQuery: BusinessQuery,
		private readonly projectsQuery: ProjectsQuery,
		private readonly assetStore: AssetStore,
		private readonly sessionQuery: SessionQuery,
		private readonly creativeUnitPackageQuery: CreativeUnitPackageQuery,
		private readonly globalService: GlobalService
	) {}

	public get(params: QueryParams, append?: boolean, returnOnly?: boolean, ...args) {
		const slug = args[0] as string;
		const collectionTarget = args[1] as string;

		switch (true) {
			case slug === 'uploads': {
				const user = this.sessionQuery.getProfile();
				const project = this.projectsQuery.getActive();

				let businessId;
				if (!this.sessionQuery.isAtLeastSuperAdmin()){
					if (user.businesses?.length) {
						businessId = user.businesses.map(b => b.id);
					} else {
						businessId = [];
					}
				}

				const requestParams = {
					...params,
					pageSize: params.limit
				};

				delete requestParams.limit;

				return this.http
					.post<DefaultResponse<any>>(`${environment.apiUrl}/asset/find`, {
						businessId,
						projectId: project?.id,
						params: requestParams
					})
					.pipe(
						map(response => ({
							data: {
								results: response.data.map(asset => ({
									...asset,
									mediaType: asset['mediaType'] || this.getMediaTypeFromUrl(asset.assetPath)
								})),
								totalResults: response.totalResults
							}
						}))
					);
			}

			case slug === 'collection' || (typeof slug === 'string' && slug.startsWith('collection-')): {
				const collectionId = collectionTarget || slug.replace('collection-', '');
				if (!collectionId) {
					throw new Error('AssetService.get requires a collectionId when using the collection slug.');
				}

				const requestParams = {
					search: params?.search || params?.query,
					filter: params?.filter,
					sort: params?.sortBy?.id,
					sortStrategy: params?.sortBy?.direction,
					page: params?.page,
					pageSize: params?.limit,
					mediaType: params?.mediaType
				};

				return this.http
					.post<{ data: any[]; totalResults: number }>(`${environment.apiUrl}/asset/find`, {
						collectionId,
						params: requestParams
					})
					.pipe(
						map(response => ({
							data: {
								cursor: undefined,
								results: response.data.map((asset: Asset) => ({
									...asset,
									mediaType: asset['mediaType'] || this.getMediaTypeFromUrl(asset.assetPath)
								})),
								totalResults: response.totalResults
							}
						}))
					);
			}

			default: {
				const requestParams = {
					...params,
					pageSize: params.limit
				};

				delete requestParams.limit;

				return this.http
					.post<DefaultResponse<any>>(`${environment.apiUrl}/data-source/${slug}/query`, {
						params: requestParams
					})
					.pipe(
						map((response: DefaultResponse<any>) => ({
							data: {
								cursor: response.data.cursor,
								results: response.data.results.map((asset: Asset) => ({
									...asset,
									mediaType: asset['mediaType'] || this.getMediaTypeFromUrl(asset.assetPath)
								})),
								totalResults: response.data.totalResults
							}
						}))
					);
			}
		}
	}

	public getMediaTypeFromUrl(url: string) {
		const ext = url?.split('.').pop();
		if (['jpg', 'jpeg', 'png', 'svg', 'gif', 'webp'].includes(ext)) {
			return 'image';
		}
		if (['mp3', 'wav'].includes(ext)) {
			return 'audio';
		}
		if (['mp4', 'mov'].includes(ext)) {
			return 'video';
		}
		if (['ttf', 'woff', 'woff2', 'otf'].includes(ext)) {
			return 'font';
		}
		return 'file';
	}


	// TODO: expand mime whitelist.
	public isImage(mimeType: string) {
		const types = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/gif', 'image/webp'];
		return types.includes(mimeType);
	}

	public isAudio(mimeType: string) {
		const types = ['audio/mpeg', 'audio/wav'];
		return types.includes(mimeType);
	}

	public isVideo(mimeType: string) {
		const types = ['video/mp4', 'video/quicktime'];
		return types.includes(mimeType);
	}

	public isWebFont(file: File) {
		const types = ['application/x-font-ttf', 'application/x-font-woff', 'application/x-font-woff2', 'application/x-font-otf'];
		const extensions = ['ttf', 'woff', 'woff2', 'otf'];
		return types.includes(file.type) || extensions.includes(file.name.split('.').pop() as string);
	}


	// TODO
	public uploadFromUrl(params: {
		url: string;
		title?: string;
		siteId: string;
		projectId?: string;
		businessId?: string;
		collectionIds?: string[];
		provider: 'amazon' | 'external';
		metadata?: Record<string, any>;
		assetId?: string;
	}): Observable<any> {
		const payload = {
			url: params.url,
			title: params.title,
			provider: params.provider,
			businessId: params.businessId,
			projectId: params.projectId,
			collectionIds: params.collectionIds,
			metadata: params.metadata,
			assetId: params.assetId
		};

		return this.http
			.post<DefaultResponse<any>>(`${environment.apiUrl}/asset/site/${params.siteId}/from-url`, payload)
			.pipe(
				tap(() => {
					this.updateAssetList(params.siteId, params.projectId, params.businessId);
				})
			);
	}

	public upload(
		fileReq: FileUploadObject,
		siteId: string,
		projectId?: string,
		businessId?: string,
		collectionIds?: string[],
		provider: string = 'amazon'
	) {
		const formData: FormData = new FormData();
		formData.append('file', fileReq.file);
		// formData.append('name', fileReq.file.name);
		formData.append('title', fileReq.title ?? fileReq.file.name);
		formData.append('provider', provider);
		formData.append('type', fileReq.category as string);
		if (fileReq.dimensions) {
			formData.append('width', fileReq.dimensions.width);
			formData.append('height', fileReq.dimensions.height);
		}
		if (fileReq.description) {
			formData.append('description', fileReq.description);
		}
		if (fileReq.saveTo === 'my-files') {
			formData.append('saveToUserFiles', 'true');
		} else {
			if (fileReq.saveTo === 'project-files' && projectId) {
				formData.append('projectId', projectId);
			}

			if (fileReq.saveTo === 'dealer-files' && businessId) {
				formData.append('businessId', businessId);
			}
		}

		const targetCollectionIds = collectionIds || fileReq.collectionIds;
		if (targetCollectionIds?.length) {
			for (const collectionId of targetCollectionIds.filter(id => !!id)) {
				formData.append('collectionIds', collectionId);
			}
		}

		return this.http
			.request(
				new HttpRequest('POST', `${environment.apiUrl}/asset/site/${siteId}`, formData, {
					reportProgress: true,
					responseType: 'json'
				})
			)
			.pipe(
				tap(() => {
					this.updateAssetList(siteId, projectId, businessId);
				})
			);
	}

	/**
	 * Uploads a file and triggers success/error notifications based on the result. Progress notifications are WIP for now.
	 * @param fileReq
	 * @param siteId
	 * @param projectId
	 * @param businessId
	 * @param collectionIds
	 * @param provider
	 */
	public uploadWithNotifications(
		fileReq: FileUploadObject,
		siteId: string,
		projectId?: string,
		businessId?: string,
		collectionIds?: string[],
		provider?: string
	) {
		return this.upload(fileReq, siteId, projectId, businessId, collectionIds, provider)
			.pipe(
				map(event => {
					if (event.type === HttpEventType.UploadProgress) {
						// TODO
					} else if (event instanceof HttpResponse) {
						// Trigger a localized success message
						const message = $localize`:@@fileUploadedSuccessfully:File saved to library.`;
						this.globalService.triggerSuccessMessage(message);
						return event.body as any;
					}
				}),
				catchError(err => {
					console.log(err);
					// Trigger a localized error message
					const message = $localize`:@@fileUploadedError:There was an issue saving your file to the library.`;
					this.globalService.triggerErrorMessage(undefined, message);
					return of(null);
				})
			)
			.toPromise();
	}

	public remove(id: string, siteId: string, projectId?: string, businessId?: string) {
		return this.http
			.request(
				new HttpRequest('DELETE', `${environment.apiUrl}/asset/site/${siteId}/${id}`, {
					reportProgress: true,
					responseType: 'json'
				})
			)
			.pipe(
				tap(() => {
					this.updateAssetList(siteId, projectId, businessId);
				})
			);
	}

	public updateAssetList(siteId: string, projectId?: string, businessId?: string, loadUserProjectExports?: boolean) {
		/*
		this.http.request<Asset[]>('GET', `${environment.apiUrl}/asset/user`).subscribe((event: Asset[]) => {
			this.assetListUser.next(event);
		});

		 */

		if (loadUserProjectExports){
			this.http.request<Asset[]>('GET', `${environment.apiUrl}/asset/user/projects`).subscribe((event: Asset[]) => {
				this.assetListUserProjectExports.next(event);
			});
		}

		this.http.request<Asset[]>('GET', `${environment.apiUrl}/asset/site/${siteId}`).subscribe((event: Asset[]) => {
			this.assetListSite.next(event);
		});

		if (projectId) {
			this.http.request<Asset[]>('GET', `${environment.apiUrl}/asset/project/${projectId}`).subscribe((event: Asset[]) => {
				this.assetListProject.next(event);
			});
		}

		if (businessId) {
			this.http.request<Asset[]>('GET', `${environment.apiUrl}/asset/business/${businessId}`).subscribe((event: Asset[]) => {
				this.assetListBusiness.next(event);
			});
		}
	}

	assetExists(assetId: string) {
		let assets: Asset[];

		combineLatest([this.assetListProject$, this.assetListBusiness$, this.assetListSite$])
			.pipe(
				map(([assetListProject, assetListBusiness, assetListSite]) => {
					return [...assetListProject, ...assetListBusiness, ...assetListSite];
			})
		).subscribe(a => assets = a);

		return assets.some(asset => asset.id === assetId);
	}

	updateAsset(id, result: any) {
		return this.http.patch(`${environment.apiUrl}/asset/${id}`, result);
	}
}
